/**
 * Flat-valley detection for cornering zones. When the median speed curve
 * stays within tolerance of its minimum over a wide stretch, the per-lap
 * min-speed LOCATION (argmin) is GPS noise, not rider inconsistency — a
 * "0 of N within 10 m" score there is median-of-locations math the rider
 * can't act on. The slowest-point row is suppressed with an explanatory
 * note instead (pass-9 spec RC3).
 */
import { SAMPLE_INTERVAL } from '../../../config/chartDisplay'
import { binMedianSpeeds } from './binSpeedSpreads'
import { type SpeedTrace } from './speedTraces'
import {
  SPEED_OK_THRESHOLD_KMH,
  BRAKE_OK_THRESHOLD_M,
} from '../../../config/consistencyScoring'
import type { ConsistencyPercentRow } from './consistencyPercent'

/** Valley wider than this ⇒ the slowest point's exact metre is unscorable. */
export const FLAT_VALLEY_MIN_WIDTH_M = 2 * BRAKE_OK_THRESHOLD_M

/**
 * Width (m) of the contiguous run of bins around the minimum-median bin
 * whose median speed is within SPEED_OK_THRESHOLD_KMH of that minimum.
 * null when no bin has a median (insufficient data).
 */
export function computeValleyWidthM(
  traces: SpeedTrace[],
  startDist: number,
  numBins: number
): number | null {
  const medians = binMedianSpeeds(traces, startDist, numBins)
  let minIdx = -1
  for (let i = 0; i < medians.length; i++) {
    const m = medians[i]
    if (m !== null && (minIdx === -1 || m < (medians[minIdx] as number))) {
      minIdx = i
    }
  }
  if (minIdx === -1) return null
  const limit = (medians[minIdx] as number) + SPEED_OK_THRESHOLD_KMH
  const inValley = (i: number): boolean => {
    const m = medians[i]
    return m !== null && m <= limit
  }
  let lo = minIdx
  while (lo - 1 >= 0 && inValley(lo - 1)) lo--
  let hi = minIdx
  while (hi + 1 < medians.length && inValley(hi + 1)) hi++
  return (hi - lo + 1) * SAMPLE_INTERVAL
}

/** Binding copy for the suppressed slowest-point row (pass-9 spec RC3). */
export function flatValleyNote(valleyWidthM: number): string {
  return `Slowest point not scored — speed is flat for ${Math.round(valleyWidthM)} m around it, so its exact location naturally wanders.`
}

export interface SuppressFlatValleyArgs {
  /** Speed traces on the same frame as the curve row (pass 29). */
  traces: SpeedTrace[]
  excludedLapIds: Set<string>
  displayBounds: { startDistance: number; endDistance: number }
}

/**
 * Drops the minSpeedLocation row (so it feeds neither zonePct, lowest,
 * isConsistent nor attribution) and notes the minSpeed row, when — and only
 * when — min speed is green AND the valley is wider than
 * FLAT_VALLEY_MIN_WIDTH_M. Cornering zones only; callers gate on zone type.
 */
export function suppressFlatValleyLocationRow(
  rows: ConsistencyPercentRow[],
  args: SuppressFlatValleyArgs
): ConsistencyPercentRow[] {
  const minSpeed = rows.find((r) => r.metric === 'minSpeed')
  const location = rows.find((r) => r.metric === 'minSpeedLocation')
  if (!minSpeed || !location || minSpeed.band !== 'green') return rows
  const included = args.traces.filter((t) => !args.excludedLapIds.has(t.id))
  const numBins = Math.floor(
    (args.displayBounds.endDistance - args.displayBounds.startDistance) /
      SAMPLE_INTERVAL
  )
  if (numBins < 1 || included.length === 0) return rows
  const width = computeValleyWidthM(
    included,
    args.displayBounds.startDistance,
    numBins
  )
  if (width === null || width <= FLAT_VALLEY_MIN_WIDTH_M) return rows
  const note = flatValleyNote(width)
  return rows
    .filter((r) => r.metric !== 'minSpeedLocation')
    .map((r) =>
      r.metric === 'minSpeed'
        ? {
            ...r,
            coverageNote: r.coverageNote ? `${r.coverageNote} · ${note}` : note,
          }
        : r
    )
}
